"use client";

import { RefObject } from "react";
import { useIsomorphicLayoutEffect } from "modules/common/hooks/useIsomorphicLayoutEffect";

/**
 * Publishes the height of a bar fixed to the bottom of the viewport as
 * `--bottom-bar`, so `data-view-panel` can end above it instead of running
 * underneath.
 *
 * Measured rather than hard-coded because the bars hide at different
 * breakpoints (the dock on city pages at `lg`, everywhere else at `md`), and a
 * padding class picked for one of them leaves the panel clipped under the other.
 * A hidden bar measures 0, so the inset tracks the breakpoints for free.
 *
 * The published value is the MAX over every mounted bar, not last-writer-wins:
 * more than one can be mounted at a time (a deck page shows two bottom
 * actions), and with a single shared custom property the later one used to
 * overwrite the taller one — and, worse, unmounting either one removed the
 * property outright, dropping the inset to 0 while the other bar was still on
 * screen and letting the panel end underneath it.
 */
const barHeights = new Map<HTMLElement, number>();

function publishBottomBarInset() {
  const root = document.documentElement;
  if (barHeights.size === 0) {
    root.style.removeProperty("--bottom-bar");
    return;
  }
  let tallest = 0;
  for (const height of barHeights.values()) {
    if (height > tallest) tallest = height;
  }
  root.style.setProperty("--bottom-bar", `${tallest}px`);
}

export function useBottomBarInset(ref: RefObject<HTMLElement | null>) {
  useIsomorphicLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;

    const measure = () => {
      barHeights.set(el, Math.round(el.getBoundingClientRect().height));
      publishBottomBarInset();
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);

    return () => {
      observer.disconnect();
      barHeights.delete(el);
      publishBottomBarInset();
    };
  }, [ref]);
}
